import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { BodyMain, ButtonLink, PhoneInput, Space, SubTitle } from '@/components';
import { useFirstRenderEffect, useTimer } from '@/hooks';
import { goalReached, YMGoals } from '@/infrastructure/ym';
import { MainLayout } from '@/layout';
import { checkConfirmationCode, sendConfirmationRequest, storeConfirmationState } from '@/model/identification/actions';
import { makeSelectConfirmationState, makeSelectPhone } from '@/model/identification/selectors';
import { ConfirmationState, IdentificationUIState } from '@/model/identification/types';

import { ConfirmationCodeInput } from './components';

const selectPhone = makeSelectPhone();
const selectConfirmationState = makeSelectConfirmationState();

export const ConfirmationPage: React.FC = () => {
  const dispatch = useDispatch();
  const [valid, setValid] = useState<boolean>(false);
  const [errorId, setErrorId] = useState<string | undefined>();
  const [code, setCode] = useState<string | undefined>();
  const phone = useSelector(selectPhone)?.value;
  const confirmationState = useSelector(selectConfirmationState);
  const [secondsTilRetry] = useTimer(confirmationState?.resendAfter);

  useFirstRenderEffect(() => goalReached(YMGoals.PHONE_CONFIRMATION_INIT));

  useEffect(() => {
    if (confirmationState?.state === ConfirmationState.WRONG_CODE) {
      setErrorId('confirmation.errors.wrong.code');
    } else if (confirmationState?.state === ConfirmationState.ATTEMPTS_NUMBER_EXHAUSTED) {
      setErrorId('confirmation.errors.attempts.number.exhausted');
    } else if (confirmationState?.state === ConfirmationState.EXPIRED) {
      setErrorId('confirmation.errors.expired');
    } else {
      setErrorId(undefined);
    }
  }, [confirmationState]);

  return (
    <MainLayout
      minHeight={300}
      buttons={{
        back: `${IdentificationUIState.LOGIN}`,
        logout: <FormattedMessage id="identification.logout.button.title" />,
        main: {
          submit: true,
          onAct: async (): Promise<void> => {
            if (code) {
              await checkConfirmationCode({ code })(dispatch);
            }
          },
          title: <FormattedMessage id="confirmation.submit.title" />,
          disabled: !valid || !!errorId || confirmationState?.state !== ConfirmationState.IN_PROGRESS,
        },
      }}
      header={{
        title: <FormattedMessage id="confirmation.title" />,
      }}
    >
      <BodyMain>
        <Space size={1} wrapItems direction="vertical">
          <FormattedMessage id="confirmation.code.description" />
          {phone && (
            <SubTitle>
              <PhoneInput value={phone} readonly shortMask />
            </SubTitle>
          )}
        </Space>
      </BodyMain>
      <ConfirmationCodeInput
        value={code}
        errorId={errorId}
        onChange={async (newCode: string): Promise<void> => {
          setCode(newCode);
          setValid(newCode.replace(/[^\d]/g, '').length === 6);
          if (confirmationState?.state === ConfirmationState.WRONG_CODE) {
            await storeConfirmationState({ ...confirmationState, state: ConfirmationState.IN_PROGRESS })(dispatch);
          }
        }}
      />
      {secondsTilRetry && secondsTilRetry > 0 ? (
        <BodyMain style={{ textAlign: 'center' }}>
          <FormattedMessage
            id="confirmation.repeat.description"
            values={{
              timer: moment.utc(moment.duration(secondsTilRetry, 's').asMilliseconds()).format('mm:ss'),
            }}
          />
        </BodyMain>
      ) : (
        <ButtonLink
          onClick={async (): Promise<void> => {
            await sendConfirmationRequest()(dispatch);
            setCode('');
            setValid(false);
          }}
          style={{ textAlign: 'center' }}
        >
          <FormattedMessage id="confirmation.repeat.link" />
        </ButtonLink>
      )}
    </MainLayout>
  );
};

export default ConfirmationPage;
